<script setup lang="ts">
    import TagsList from '@shared/components/Tags/TagsList.vue';
    import { formatDateTime } from '@shared/utils/formatDateTime';
    import { formatNumericText } from '@shared/utils/formatNumericText';
    import type { SurveyStatisticsSource } from '../../SurveysTypes.d';

    const props = defineProps<{
        source: SurveyStatisticsSource;
    }>();

    defineEmits<{
        (e: 'show-preview'): void;
        (e: 'show-respondents'): void;
        (e: 'show-non-respondents'): void;
    }>();

    const nonRespondentCount = props.source.RecipientCount - props.source.RespondentCount;
</script>

<template>
    <div class="flex-1" style="min-width: min(400px, 100%)">
        <h2 class="h4 item-heading">
            <a :href="`/Issues/${source.IssueId}/Statistics`">{{ source.Subject }}</a>
            <small v-if="source.Category?.Name">{{ source.Category.Name }}</small>
            <button type="button" class="btn btn-subtle btn-sm" v-tooltip.top="$Resources.IssuePreviewButton" @click="$emit('show-preview')">
                <span class="glyphicon glyphicon-eye-open" aria-hidden="true"></span>
                <span class="sr-only">{{ $Resources.IssuePreviewButton }}</span>
            </button>
        </h2>

        <p v-if="source.IssueName">{{ source.IssueName }}</p>

        <ul class="list-inline list-unstyled m-sm-b">
            <li class="p-0-r">{{ $Resources.RecipientsLabel }}:</li>
            <li v-for="seg in source.Segments" :key="seg.SegmentId">
                <a :href="`/Segments/${seg.SegmentId}`" target="_blank" rel="noopener">{{ seg.Name }}</a>
                <span class="mini-label mini-label-info m-xs-l">{{ $Resources.SegmentLabel }}</span>
            </li>
            <li v-for="list in source.Lists" :key="list.ListId">
                <a :href="`/Lists/${list.ListId}`" target="_blank" rel="noopener">{{ list.Name }}</a>
                <span class="mini-label mini-label-info m-xs-l">{{ $Resources.ListLabel }}</span>
            </li>
        </ul>

        <TagsList v-if="source.Tags.length > 0" :tags="source.Tags" readonly></TagsList>

        <p class="meta">
            <i class="fa fa-pencil" aria-hidden="true"></i>
            {{ $Resources.LastModifiedLabel }}
            <time :datetime="source.LastModified">{{ formatDateTime(source.LastModified) }}</time>
            <template v-if="source.LastModifiedBy">
                {{ $Resources.GenericByLabel }}
                {{ source.LastModifiedBy.Name }}
            </template>
        </p>
    </div>

    <div class="align-self-center" style="min-width: 280px">
        <div class="ug-values d-flex justify-content-between">
            <div class="ug-value">
                <span class="ug-value-label">{{ $Resources.SurveyResponseCountLabel }}</span>
                {{ formatNumericText(source.ResponseCount, { largeNumbers: true }) }}
            </div>
            <div class="ug-value">
                <span class="ug-value-label">{{ $Resources.SurveyRespondentCountLabel }}</span>
                <a href="#" @click.prevent="$emit('show-respondents')">
                    {{ formatNumericText(source.RespondentCount, { largeNumbers: true }) }}
                </a>
            </div>
            <div class="ug-value">
                <span class="ug-value-label">{{ $Resources.SurveyNonRespondentsCountLabel }}</span>
                <a href="#" @click.prevent="$emit('show-non-respondents')">
                    {{ formatNumericText(nonRespondentCount, { largeNumbers: true }) }}
                </a>
            </div>
        </div>
    </div>
</template>
